import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSAutocomplete from "views/system-design/DSAutocomplete";
import DSAvatar from "views/system-design/DSAvatar";
import DSTextField from "views/system-design/DSTextField";
import PersonIcon from "@mui/icons-material/Person";
import { Author } from "api/author";
import { getInitials, getAvatarColor } from "../../utils";
import SectionHeader from "./SectionHeader";

interface UserPickerFieldProps {
  label: string;
  placeholder: string;
  selectedUser: Author | null;
  options: Author[];
  onChange: (user: Author | null) => void;
  listboxMaxHeight?: number;
}

export default function UserPickerField({
  label,
  placeholder,
  selectedUser,
  options,
  onChange,
  listboxMaxHeight = 250,
}: UserPickerFieldProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <DSBox sx={{ flex: 1, position: "relative" }}>
      <SectionHeader
        icon={<PersonIcon sx={{ fontSize: 16 }} />}
        title={label}
      />
      <DSBox
        role="button"
        tabIndex={0}
        onClick={() => setDropdownOpen(true)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setDropdownOpen(true);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          p: 1,
          borderRadius: 2,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <DSAvatar
          sx={{
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 600,
            bgcolor: selectedUser
              ? getAvatarColor(selectedUser.name)
              : "white.16",
            ...(selectedUser ? {} : { color: "text.secondary" }),
          }}
        >
          {selectedUser ? getInitials(selectedUser.name) : "?"}
        </DSAvatar>
        <DSTypography
          variant="body2"
          sx={{
            color: selectedUser ? "text.primary" : "text.secondary",
          }}
        >
          {selectedUser?.name.trim() ?? placeholder}
        </DSTypography>
      </DSBox>
      {dropdownOpen && (
        <>
          <DSBox
            onClick={() => setDropdownOpen(false)}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1299,
            }}
          />
          <DSBox
            sx={{
              position: "absolute",
              zIndex: 1300,
              left: 0,
              bottom: "100%",
              mb: 0.5,
              width: 320,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 8,
            }}
          >
            <DSAutocomplete<Author>
              open
              options={options}
              getOptionLabel={(option) => option.name.trim()}
              value={selectedUser}
              onChange={(_event, selected) => {
                onChange(selected);
                setDropdownOpen(false);
              }}
              onClose={() => setDropdownOpen(false)}
              renderOption={(props, option) => (
                <li {...props}>
                  <DSBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <DSAvatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: getAvatarColor(option.name),
                      }}
                    >
                      {getInitials(option.name)}
                    </DSAvatar>
                    <DSTypography variant="body2">{option.name}</DSTypography>
                  </DSBox>
                </li>
              )}
              renderInput={(params) => (
                <DSTextField
                  {...params}
                  placeholder="Search user..."
                  fullWidth
                  size="small"
                  autoFocus
                />
              )}
              disablePortal
              ListboxProps={{ sx: { maxHeight: listboxMaxHeight } }}
              sx={{ p: 1 }}
            />
          </DSBox>
        </>
      )}
    </DSBox>
  );
}
